import React from 'react';
import { Empty } from 'app/seedly-component-library';
import { CornerUpRight, Eye } from 'react-feather';
import {
  useCommentById,
  useUserComments,
  useCommentApiStatus,
} from 'entity/comment/commentHooks';
import {
  CommentParentEntityClient,
  CommentRelationMap,
  ParentCommentTypes,
} from 'entity/comment/commentTypes';
import Button from 'seedly-component-library/button-new';
import { useRouter } from 'next/router';
import buildConfig from 'utils/buildConfig';
import CommentItemWithChild from '../CommentItemWithChild';
import CommentItem from '../CommentItem';

const makeEntityLink = (entity: CommentParentEntityClient) => {
  if (entity.commentableType === 'Opinion') {
    return `/opinions/${entity.slug}`;
  }

  if (entity.commentableType === 'Post') {
    return `/posts/${entity.slug}`;
  }

  if (entity.commentableType === 'Review') {
    return `/reviews/${entity.category?.slug}/${entity.product?.slug}`;
  }

  if (entity.commentableType === 'Article') {
    return `${buildConfig.blogHost}/${entity.slug}`;
  }

  return '';
};

const formatTitle = (title: string) => {
  return title.length > 170 ? `${title.substring(0, 170)}...` : title;
};

const CommentListWithEntity = props => {
  const { handleLoadMoreComment, customEmpty } = props;

  const apiStatus = useCommentApiStatus();
  const { entityCommentTuples, pagination } = useUserComments();
  const commentById = useCommentById();
  const router = useRouter();

  const { isFetching } = apiStatus;
  const hasMorePage = pagination.totalPage > pagination.currentPage;

  const shouldRenderLoadmore =
    !isFetching && hasMorePage && handleLoadMoreComment;

  const commentList = entityCommentTuples.map(entityCommentTuple => {
    const [parentEntity, commenId] = entityCommentTuple;
    const comment = commentById[commenId];
    const isParentComment = ParentCommentTypes.includes(
      parentEntity.commentableType,
    );
    const entityLink = isParentComment
      ? makeEntityLink(parentEntity)
      : parentEntity.link;

    const title = isParentComment
      ? formatTitle(parentEntity.title)
      : formatTitle(parentEntity.commentText);

    return (
      <div key={commenId} className="card h-fit">
        <div className="flex flex-col gap-4 w-full">
          {comment.isAnonymous && (
            <div className="flex gap-2 items-center">
              <Eye size={14} className="text-neutral-500" />
              <p className="text-xs text-neutral-500">Only visible to you</p>
            </div>
          )}
          <div className="flex items-center gap-1">
            <CornerUpRight className="min-h-[16px] min-w-[16px]" size={16} />
            <a
              className="text-xs font-bold break-all hover:text-blue-500"
              href={entityLink}
              target="_blank"
              rel="noopener noreferrer"
            >
              {title}
            </a>
          </div>
          {isParentComment && <div className="horizontal-divider" />}
          {isParentComment ? (
            <CommentItemWithChild
              commentableType={parentEntity.commentableType}
              childCommentableType={
                CommentRelationMap[parentEntity.commentableType]
              }
              comment={comment}
              commentShareLink={`${buildConfig.apiHost}${entityLink}`}
            />
          ) : (
            <div className="pl-2 relative w-[92%] left-6 border-l-[1px] border-solid border-neutral-400">
              <CommentItem
                commentableType={parentEntity.commentableType}
                commentShareLink={`${buildConfig.apiHost}${entityLink}`}
                comment={comment}
                onReplyClick={() => {
                  router.push(entityLink);
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  });

  return (
    <div className="w-full">
      <div className="list">
        {commentList.length > 0
          ? commentList
          : customEmpty || <Empty title="No Posts Found." />}
      </div>
      {shouldRenderLoadmore && !isFetching && (
        <div className="flex item-center justify-center mt-2">
          <Button variant="ghost" onClick={handleLoadMoreComment}>
            Load more comments
          </Button>
        </div>
      )}
    </div>
  );
};

export default CommentListWithEntity;
